"use strict";
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PlayerManager = void 0;
const interfaces_1 = require("../../../shared/interfaces");
const rxjs_1 = require("rxjs");
const store_1 = require("../../../shared/store");
const lodash_1 = __importDefault(require("lodash"));
const settings_1 = require("../config/settings");
const Global_1 = require("../Global");
const levels_1 = require("../../../shared/levels");
class PlayerManager {
    constructor(gameManager) {
        this.gameManager = gameManager;
        this.playerData = new rxjs_1.BehaviorSubject(null);
        this.playerData$ = this.playerData.asObservable();
        this.playerStat$ = new rxjs_1.Subject();
        this.firsttime$ = new rxjs_1.BehaviorSubject(null);
        this.options$ = new rxjs_1.BehaviorSubject(settings_1.DefaultOptions);
        this.localdata = null;
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.loadLocalPlayer();
        });
    }
    get data() {
        return this.playerData.value;
    }
    get level() {
        var _a;
        const lvl = (_a = this.data) === null || _a === void 0 ? void 0 : _a.lvl;
        return lvl ? levels_1.levels[lvl] : levels_1.levels[0];
    }
    updateLabel(info) {
        this.gameManager.send(interfaces_1.SocketAction.ChangeLabel, info);
    }
    buy(name, value = null) {
        this.gameManager.send(interfaces_1.SocketAction.Buy, { name, value });
    }
    reward() {
        // Would be nice to get a token here to check on the backend to make sure people are not hacking...
        // It is a limitation of Poki
        this.gameManager.send(interfaces_1.SocketAction.Reward);
    }
    ownsAnything() {
        var _a;
        const mystore = (_a = this.data) === null || _a === void 0 ? void 0 : _a.store;
        return mystore && !lodash_1.default.isEqual(store_1.defaultStore, mystore);
    }
    owns(name, item) {
        var _a, _b;
        const target = (_b = (_a = this.data) === null || _a === void 0 ? void 0 : _a.store) === null || _b === void 0 ? void 0 : _b[name];
        if (target) {
            return (target.indexOf(item) !== -1);
        }
        return false;
    }
    get brushes() {
        var _a;
        return ((_a = this.data) === null || _a === void 0 ? void 0 : _a.brushes) || 0;
    }
    get streak() {
        var _a;
        return ((_a = this.data) === null || _a === void 0 ? void 0 : _a.streak) || 0;
    }
    get pkey() {
        var _a;
        return (_a = this.data) === null || _a === void 0 ? void 0 : _a.pkey;
    }
    lang() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return (yield this.getItem('i18nextLng'));
            }
            catch (e) { }
            return null;
        });
    }
    get nativeStorage() {
        return window.NativeStorage || null;
    }
    setItem(key, value) {
        return __awaiter(this, void 0, void 0, function* () {
            if ((0, Global_1.isDevice)()) {
                return yield new Promise((resolve, reject) => {
                    var _a;
                    (_a = this.nativeStorage) === null || _a === void 0 ? void 0 : _a.setItem(key, value, resolve, reject);
                });
            }
            else {
                let stringed;
                try {
                    stringed = JSON.stringify(value);
                }
                catch (e) {
                    stringed = value;
                }
                localStorage.setItem(key, stringed);
            }
            return value;
        });
    }
    getItem(key) {
        return __awaiter(this, void 0, void 0, function* () {
            if ((0, Global_1.isDevice)()) {
                // Migrate from past items (stored using other methods) can probably delete in several months (2024-10-01)
                yield this.migrate(key);
                return yield new Promise((resolve, reject) => {
                    var _a;
                    (_a = this.nativeStorage) === null || _a === void 0 ? void 0 : _a.getItem(key, resolve, reject);
                });
            }
            else {
                const info = localStorage.getItem(key);
                if (info) {
                    try {
                        return JSON.parse(info);
                    }
                    catch (e) {
                        return info;
                    }
                }
                return null;
            }
        });
    }
    // Temporary, migrating from old local storage to new native local storage for old players
    migrate(key) {
        return __awaiter(this, void 0, void 0, function* () {
            let past = localStorage.getItem(key);
            if (past) {
                // Move to other storage
                try {
                    let p;
                    try {
                        p = JSON.parse(past);
                    }
                    catch (e) {
                        p = past;
                    }
                    yield this.setItem(key, p);
                    // Delete the old one
                    localStorage.removeItem(key);
                    return true;
                }
                catch (e) {
                    console.error(e);
                }
            }
            return false;
        });
    }
    didDraw(term) {
        var _a;
        const li = (0, levels_1.lvlForTerm)(term);
        if (this.data && levels_1.levels[li] && this.data.lvl !== -1) {
            if (li < this.data.lvl) {
                return true;
            }
            else if (li > this.data.lvl) {
                return false;
            }
            else if (li === this.data.lvl) {
                const tidx = levels_1.levels[li].terms.indexOf(term);
                return ((_a = this.data.lvlf) === null || _a === void 0 ? void 0 : _a.indexOf(tidx)) !== -1;
            }
        }
        return false;
    }
    setData(pd) {
        return __awaiter(this, void 0, void 0, function* () {
            const cur = this.data;
            if (cur && pd) {
                const brushchange = pd.brushes - cur.brushes;
                const streakchange = pd.streak - cur.streak;
                const levelchange = pd.lvl - cur.lvl;
                if (brushchange || streakchange || levelchange) {
                    this.playerStat$.next({
                        brushes: brushchange,
                        lvl: levelchange,
                        streak: streakchange
                    });
                }
            }
            this.playerData.next(yield this.saveLocalPlayer(pd));
        });
    }
    setOption(item, value) {
        return __awaiter(this, void 0, void 0, function* () {
            const cur = this.options$.value;
            cur[item] = value;
            const next = (yield this.setItem(PlayerManager.OptionsKey, cur));
            this.options$.next(next);
            return next;
        });
    }
    saveLocalPlayer(playerData) {
        return __awaiter(this, void 0, void 0, function* () {
            if (playerData) {
                // We really only need to save our pkey, saves data and doesn't let the user see everything
                const mylang = yield this.lang();
                const simple = {
                    pkey: playerData.pkey || null,
                    secret: playerData.secret || null,
                    lang: mylang || settings_1.Settings.defaultLang
                };
                this.localdata = simple;
                yield this.setItem(PlayerManager.StorageKey, simple);
            }
            return playerData;
        });
    }
    /**
     * Loads the player info from local storage
     * @private
     */
    loadLocalPlayer() {
        return __awaiter(this, void 0, void 0, function* () {
            let data;
            try {
                data = (yield this.getItem(PlayerManager.StorageKey));
            }
            catch (e) {
                console.warn(e, 'No local player found');
                data = null;
            }
            const amthere = (data === null || data === void 0 ? void 0 : data.pkey) && (data === null || data === void 0 ? void 0 : data.secret);
            // Load local game settings
            try {
                const ops = (yield this.getItem(PlayerManager.OptionsKey));
                if (ops) {
                    this.options$.next(ops);
                }
            }
            catch (e) { }
            if (this.firsttime$.value === null) {
                this.firsttime$.next(!amthere);
            }
            if (amthere) {
                this.localdata = data;
                return this.localdata;
            }
            return null;
        });
    }
    join(since) {
        return __awaiter(this, void 0, void 0, function* () {
            const player = yield this.loadLocalPlayer();
            const room = this.gameManager.room$.value;
            this.gameManager.send(interfaces_1.SocketAction.Join, Object.assign(Object.assign({}, player), {
                lang: (player === null || player === void 0 ? void 0 : player.lang) || (yield this.lang()),
                rkey: room === null || room === void 0 ? void 0 : room.rkey,
                since: since,
                time: (new Date()).toISOString()
            }));
            return yield (0, rxjs_1.firstValueFrom)(this.playerData.asObservable().pipe((0, rxjs_1.skip)(1)));
        });
    }
    refresh() {
        return __awaiter(this, void 0, void 0, function* () {
            // Ask the server to send back the latest player (should probably move this to the server side)
            this.gameManager.send(interfaces_1.SocketAction.ChangedPlayer);
            return yield (0, rxjs_1.firstValueFrom)(this.playerData.asObservable().pipe((0, rxjs_1.skip)(1)));
        });
    }
}
exports.PlayerManager = PlayerManager;
PlayerManager.StorageKey = 'player';
PlayerManager.OptionsKey = 'options';
